import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Building2 } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Tooltip } from '@/components/Tooltip'
import { useDismissedBanner } from '@/hooks/useDismissedBanner'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, extractFieldErrors, formatSaveError } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { ClientsFile, DocumentTypesFile, Firm, GlobalDefaultsFile, ProductTypesFile } from '@/types/bundle'
import { EnabledProductsEditor } from './components/EnabledProductsEditor'
import { MaskedApiKeyField } from './components/MaskedApiKeyField'
import { OverridesSection } from './components/OverridesSection'
import { UsagePlanEditor } from './components/UsagePlanEditor'
import {
  expandAllowlistToConcreteFields,
  getFirmAuthDisplayValue,
  matchFieldError,
  MASKED_API_KEY_PLACEHOLDER,
  removeOverrideValue,
  setOverrideValue,
} from './overrideUtils'

const BANNER_STORAGE_KEY = 'legalflow-config-client-overrides-banner-dismissed'

interface LocationState {
  initialEntry?: Firm
}

function SkeletonBlock() {
  return <div className="h-96 animate-pulse rounded-control bg-surface-raised" />
}

const CLIENTS_QUERY_KEY = ['draft-file', 'clients']

export function ClientDetailScreen() {
  const { firmId = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const initialEntry = (location.state as LocationState | null)?.initialEntry

  const clientsQuery = useQuery({
    queryKey: CLIENTS_QUERY_KEY,
    queryFn: () => getDraftFile<ClientsFile>('clients'),
  })
  const globalDefaultsQuery = useQuery({
    queryKey: ['draft-file', 'global_defaults'],
    queryFn: () => getDraftFile<GlobalDefaultsFile>('global_defaults'),
  })
  const productTypesQuery = useQuery({
    queryKey: ['draft-file', 'product_types'],
    queryFn: () => getDraftFile<ProductTypesFile>('product_types'),
  })
  const documentTypesQuery = useQuery({
    queryKey: ['draft-file', 'document_types'],
    queryFn: () => getDraftFile<DocumentTypesFile>('document_types'),
  })

  const [draft, setDraft] = useState<ClientsFile | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<ClientsFile | null>(null)
  const [bannerDismissed, dismissBanner] = useDismissedBanner(BANNER_STORAGE_KEY)

  // Seeds the local editable draft, scoped to just this one firm, and
  // follows server changes (hooks/useServerSeed.ts). A brand-new/duplicated
  // firm (absent from the file, initialEntry supplied by the list screen's
  // New/Duplicate modal) is synthesized client-side - a real unsaved draft,
  // never written to the backend until Save.
  const serverSeed = useServerSeed(CLIENTS_QUERY_KEY, clientsQuery, {
    seed: (data) => {
      if (data.firms[firmId]) {
        setDraft(data)
        setSavedSnapshot(data)
      } else if (initialEntry) {
        setDraft({ ...data, firms: { ...data.firms, [firmId]: initialEntry } })
        setSavedSnapshot(data)
      }
    },
    isDirty: () => draft !== null && JSON.stringify(draft.firms[firmId]) !== JSON.stringify(savedSnapshot?.firms[firmId]),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: ClientsFile) => putDraftFile<ClientsFile>('clients', payload),
    onSuccess: (_result, payload) => {
      // Reset dirty state against the snapshot just saved (same pattern as
      // every other screen) rather than waiting on the invalidated query's
      // async refetch. Note: for a firm whose key was genuinely just
      // rotated, this locally echoes the real value back into state for one
      // render until the invalidated query refetches the server's masked
      // truth - harmless (the key already passed through the browser once
      // when typed), not a new leak, but real until that refetch lands.
      setDraft(payload)
      setSavedSnapshot(payload)
      // No serverSeed.noteSaved here: the payload can hold a just-typed real
      // API key, which must not go into the shared query cache. The refetch
      // below brings the server's masked copy and re-seeds from it.
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'clients' })
    },
  })

  if (
    clientsQuery.isPending ||
    globalDefaultsQuery.isPending ||
    productTypesQuery.isPending ||
    documentTypesQuery.isPending
  ) {
    return <SkeletonBlock />
  }

  if (clientsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load client overrides">
        {errorMessage(clientsQuery.error)}
      </Banner>
    )
  }
  if (globalDefaultsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load platform defaults">
        {errorMessage(globalDefaultsQuery.error)}
      </Banner>
    )
  }
  if (productTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load product types">
        {errorMessage(productTypesQuery.error)}
      </Banner>
    )
  }
  if (documentTypesQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load document types">
        {errorMessage(documentTypesQuery.error)}
      </Banner>
    )
  }

  const existingFirm = clientsQuery.data.firms[firmId]

  if (!existingFirm && !initialEntry) {
    return (
      <EmptyState
        icon={Building2}
        headline="Firm not found"
        description="This firm doesn't exist in the current draft or live bundle."
        action={{ label: 'Back to Clients', onClick: () => navigate('/client-overrides') }}
      />
    )
  }

  if (!draft) {
    return <SkeletonBlock />
  }

  const allFields = expandAllowlistToConcreteFields(
    globalDefaultsQuery.data.override_governance,
    productTypesQuery.data,
    documentTypesQuery.data,
  )

  const firm = draft.firms[firmId]
  // A firm not present in the last-fetched server truth was added this
  // session and has never been saved - it has no prior real key to mask/
  // replace at all (Core logic step 3), independent of what its `auth.
  // api_key` happens to hold locally right now.
  const isNewFirm = !clientsQuery.data.firms[firmId]
  const isDirty = JSON.stringify(draft.firms[firmId]) !== JSON.stringify(savedSnapshot?.firms[firmId])
  const hasEmptyKey = getFirmAuthDisplayValue(firm).trim().length === 0

  const fieldErrors = extractFieldErrors(saveMutation.error)
  const apiKeyErrorPath = `firms.${firmId}.auth.api_key`
  const unmatchedErrors = fieldErrors?.filter((err) => {
    if (err.path === apiKeyErrorPath) return false
    return !allFields.some((field) => matchFieldError(err, firmId, field))
  })

  function updateFirm(updater: (firm: Firm) => Firm) {
    setDraft((current) => {
      if (!current) return current
      return { ...current, firms: { ...current.firms, [firmId]: updater(current.firms[firmId]) } }
    })
  }

  async function handleSave() {
    if (!draft || hasEmptyKey || !(await serverSeed.ensureCurrent())) return
    saveMutation.mutate(draft)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Link to="/client-overrides" className="text-text-secondary hover:text-text-primary">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Link>
        <h1 className="text-title font-semibold text-text-primary">{firm.name}</h1>
      </div>

      {!bannerDismissed && (
        <Banner
          variant="info"
          icon={Building2}
          title="Only platform-allowed fields can be overridden here"
          onDismiss={dismissBanner}
        >
          The Override Engine fails closed on anything outside the platform's allow-list and bounds - this
          screen only ever shows fields it would actually accept.
        </Banner>
      )}

      {serverSeed.baseChanged && <StaleDraftBanner onReload={serverSeed.reload} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error, unmatchedErrors)}
        </Banner>
      )}

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <div className="grid grid-cols-3 items-start gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-meta font-medium text-text-secondary">Firm name</span>
            <span className="text-body text-text-primary">{firm.name}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Firm ID
              <InfoTooltip text="A permanent internal identifier for this firm. It can't be changed later." />
            </span>
            <span className="font-mono text-body text-text-primary">{firmId}</span>
          </div>
          <label className="flex items-center gap-2 justify-self-end">
            <input
              type="checkbox"
              checked={firm.enabled ?? false}
              onChange={(event) => updateFirm((current) => ({ ...current, enabled: event.target.checked }))}
              className="accent-accent"
            />
            <span className="text-body text-text-primary">Enabled</span>
            <InfoTooltip text="Turns this firm on or off. A disabled firm can't process any requests." />
          </label>
        </div>

        <EnabledProductsEditor
          firm={firm}
          productTypes={productTypesQuery.data}
          onChange={(enabled_products) => updateFirm((current) => ({ ...current, enabled_products }))}
        />

        <UsagePlanEditor
          firm={firm}
          productTypes={productTypesQuery.data}
          onChange={(usage_plan) => updateFirm((current) => ({ ...current, usage_plan }))}
        />

        <MaskedApiKeyField
          value={getFirmAuthDisplayValue(firm)}
          onChange={(value) =>
            updateFirm((current) =>
              // The placeholder means "no real change" (matches whichever
              // auth shape the firm already has - a raw masked api_key or a
              // migrated api_key_secret_ref) - never write it back, since a
              // migrated firm has no current api_key for the backend's own
              // unmask-on-write logic to restore (it would reject the save
              // outright). A genuinely new value always replaces the whole
              // auth block outright, dropping any stale api_key_secret_ref -
              // app/core/auth.py resolves api_key_secret_ref first when both
              // are present, so leaving an old one in place would make the
              // freshly-typed key silently never take effect.
              value === MASKED_API_KEY_PLACEHOLDER ? current : { ...current, auth: { scheme: 'api_key', api_key: value } },
            )
          }
          isNewFirm={isNewFirm}
          firmId={firmId}
          error={fieldErrors?.find((err) => err.path === apiKeyErrorPath)?.message}
        />
      </section>

      <OverridesSection
        firm={firm}
        allFields={allFields}
        productTypes={productTypesQuery.data}
        onSetValue={(field, value) => updateFirm((current) => setOverrideValue(current, field, value))}
        onRemove={(field) => updateFirm((current) => removeOverrideValue(current, field))}
        serverErrorFor={(field) => fieldErrors?.find((err) => matchFieldError(err, firmId, field))?.message}
      />

      <div className="flex justify-end">
        {(() => {
          const disabledReason = !isDirty
            ? 'No changes to save'
            : serverSeed.baseChanged
              ? STALE_DRAFT_SAVE_REASON
              : hasEmptyKey
              ? 'This firm needs an API key before saving'
              : null

          if (disabledReason && !saveMutation.isPending) {
            return (
              <Tooltip text={disabledReason}>
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Save
                </button>
              </Tooltip>
            )
          }

          return (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={handleSave}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
        })()}
      </div>
    </div>
  )
}
